import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { EmptyTimeEntryCell } from './EmptyTimeEntryCell'
import { TimeEntryCell } from './TimeEntryCell'
import { useMyTimesData } from './useMyTimesData'
import { ViewLogTimeDrawer } from './ViewLogTimeDrawer'
import { TimeTrackingControlBar } from '../../components/TimeTrackingControlBar'
import { TIME_TRACKING_CONSTANTS } from '../../const'
import {
  TimeTrackingTab,
  FilterValues,
  MyTimesTabProps,
  WeekDay,
} from '../../types'
import {
  getCurrentWeekRange,
  getWeekDates,
  navigateWeek,
} from '../../utils/dateUtils'
import {
  calculateDayTotal,
  calculateWeekBreakdown,
  calculateWeekTotal,
} from '../../utils/weekCalculations'
import { ConfirmActionModal } from '../components/ConfirmActionModal'
import { Badge } from '@/Atomic/Atoms/Badge'
import { Button } from '@/Atomic/Molecules/Button'
import {
  deleteTimeRecord,
  duplicateTimeRecord,
  getTimeRecordViewData,
  TimeRecordViewData,
} from '@/Widgets/TimeTrackingOverviewWidget/utils/timerecordMethods'

interface DrawerState {
  isOpen: boolean
  recordId: string | null
  data: TimeRecordViewData | null
}

interface MyTimesTabInnerProps extends MyTimesTabProps {
  drawerState: DrawerState
  setDrawerState: React.Dispatch<React.SetStateAction<DrawerState>>
}

const MyTimesTabInner = connect(
  ({
    openLogTimeDrawer,
    initialFilters,
    weekRange,
    onWeekRangeChange,
    onFilterChange,
    drawerState,
    setDrawerState,
    currentUserId,
  }: MyTimesTabInnerProps) => {
    const { t } = useTranslation()
    const filters: FilterValues = initialFilters || {
      status: null,
      team: null,
      customer: null,
      order: null,
      workPackage: null,
    }

    const { timeRecords } = useMyTimesData(currentUserId, filters, weekRange)

    const [showTooltip, setShowTooltip] = useState(false)
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

    const handleLogTime = () => {
      openLogTimeDrawer()
    }

    const handleDeleteTimeRecord = (recordId: string) => {
      setPendingDeleteId(recordId)
    }

    const confirmDelete = () => {
      if (pendingDeleteId) deleteTimeRecord(pendingDeleteId)
      setPendingDeleteId(null)
    }

    const handleDuplicateTimeRecord = (recordId: string) => {
      duplicateTimeRecord(recordId)
    }

    const handleViewTimeRecord = async (recordId: string) => {
      const recordData = await getTimeRecordViewData(recordId)
      if (recordData) {
        setDrawerState({ isOpen: true, recordId, data: recordData })
      }
    }

    const handleAddNewTimeRecordForTheSameDay = (day: WeekDay) => {
      openLogTimeDrawer(undefined, {
        date: day.date
          ? new Date(day.date).toISOString()
          : new Date().toISOString(),
      })
    }

    const weekDays = getWeekDates(weekRange)
    const weekTotal = calculateWeekTotal(timeRecords)
    const weekBreakdown = calculateWeekBreakdown(timeRecords)

    const handleNavigateWeek = (direction: 'prev' | 'next') => {
      const newRange = navigateWeek(weekRange, direction)
      onWeekRangeChange(newRange)
    }

    const goToCurrentWeek = () => {
      onWeekRangeChange(getCurrentWeekRange())
    }

    return (
      <div>
        <TimeTrackingControlBar
          weekRange={weekRange}
          statusOptions={[]}
          teamOptions={[]}
          onWeekChange={onWeekRangeChange}
          onNavigateWeek={handleNavigateWeek}
          onCurrentWeek={goToCurrentWeek}
          onFilterChange={onFilterChange}
          onLogTime={handleLogTime}
          currentTab={TimeTrackingTab.MY_TIMES}
        />

        <div className="time-tracking-table-container">
          <table className="time-tracking-table">
            <thead>
              <tr>
                <th className="task-column">{t('timeTracking.task')}</th>
                {weekDays.map((day) => {
                  const dayTotal = day.date
                    ? calculateDayTotal(timeRecords, day.date)
                    : 0
                  const percentage = Math.min(
                    (dayTotal / TIME_TRACKING_CONSTANTS.EXPECTED_DAY_HOURS) *
                      100,
                    100,
                  )
                  const isOvertime =
                    dayTotal > TIME_TRACKING_CONSTANTS.EXPECTED_DAY_HOURS

                  return (
                    <th key={day.key}>
                      <div className="th-day-label">{day.label}</div>
                      <div className="th-day-hours">
                        {dayTotal}h{' '}
                        <span className="th-day-total">{day.total}</span>
                      </div>
                      <div className="th-day-progress">
                        <div
                          className={`th-day-progress-bar ${isOvertime ? 'overtime' : ''}`}
                          style={
                            {
                              '--progress-width': percentage,
                            } as React.CSSProperties
                          }
                        />
                      </div>
                    </th>
                  )
                })}
                <th className="total-column">
                  <div className="th-day-label">{t('timeTracking.total')}</div>
                  <div
                    className="th-day-hours"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    {weekTotal}h{' '}
                    <span className="th-day-total">
                      /{TIME_TRACKING_CONSTANTS.EXPECTED_WEEK_HOURS}h
                    </span>
                    {showTooltip && (
                      <div className="time-tracking-tooltip">
                        <div className="time-tracking-tooltip-item">
                          <Badge
                            variant="success"
                            value={t('timeTracking.billable')}
                          />
                          <span className="time-tracking-tooltip-value">
                            {weekBreakdown.billable}h
                          </span>
                        </div>
                        <div className="time-tracking-tooltip-item">
                          <Badge
                            variant="warning"
                            value={t('timeTracking.notBillable')}
                          />
                          <span className="time-tracking-tooltip-value">
                            {weekBreakdown.nonBillable}h
                          </span>
                        </div>
                        <div className="time-tracking-tooltip-item">
                          <Badge
                            variant="danger"
                            value={t('timeTracking.missing')}
                          />
                          <span className="time-tracking-tooltip-value">
                            {weekBreakdown.missing}h
                          </span>
                        </div>
                        <div className="time-tracking-tooltip-item">
                          <Badge
                            variant="info"
                            value={t('timeTracking.billing.utilization')}
                          />
                          <span className="time-tracking-tooltip-value">
                            {weekBreakdown.utilization}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="th-day-progress">
                    <div
                      className={`th-day-progress-bar ${weekTotal > TIME_TRACKING_CONSTANTS.EXPECTED_WEEK_HOURS ? 'overtime' : ''}`}
                      style={
                        {
                          '--progress-width': Math.min(
                            (weekTotal /
                              TIME_TRACKING_CONSTANTS.EXPECTED_WEEK_HOURS) *
                              100,
                            100,
                          ),
                        } as React.CSSProperties
                      }
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {timeRecords.length === 0 ? (
                <tr>
                  <td
                    colSpan={weekDays.length + 2}
                    className="time-tracking-empty-state"
                  >
                    <div>
                      <div className="time-tracking-empty-text">
                        {t('timeTracking.emptyStateMessage')}
                      </div>
                      <Button
                        variant="outlined"
                        title={t('timeTracking.logTime')}
                        icon="add"
                        onClick={handleLogTime}
                      />
                    </div>
                  </td>
                </tr>
              ) : (
                timeRecords.map((record) => (
                  <tr key={record.taskId}>
                    <td className="task-info">
                      <div className="task-project">{record.projectName}</div>
                      <div className="task-title">{record.projectCode}</div>
                      <div className="task-details">{record.taskName}</div>
                    </td>
                    {weekDays.map((day) => {
                      const dateKey = day.date ? day.date.split('T')[0] : ''
                      const entries = dateKey
                        ? record.dailyEntries[dateKey]
                        : undefined
                      const handleAdd = () =>
                        openLogTimeDrawer(undefined, {
                          customer: record.clientId,
                          order: record.orderId,
                          workPackage: record.positionId,
                          date: day.date
                            ? new Date(day.date).toISOString()
                            : new Date().toISOString(),
                          productType: record.productType,
                        })

                      if (!entries || entries.length === 0) {
                        return (
                          <EmptyTimeEntryCell key={day.key} onAdd={handleAdd} />
                        )
                      }

                      return (
                        <td
                          key={day.key}
                          className="time-entry-cell time-entry-cell-stacked"
                        >
                          {entries.map((entry) => (
                            <TimeEntryCell
                              key={entry.recordId}
                              hours={entry.hours}
                              activityType={entry.activityType}
                              description={entry.description}
                              onEdit={() => openLogTimeDrawer(entry.recordId)}
                              onCopy={() =>
                                handleDuplicateTimeRecord(entry.recordId)
                              }
                              onDelete={() =>
                                handleDeleteTimeRecord(entry.recordId)
                              }
                              onInfo={() =>
                                handleViewTimeRecord(entry.recordId)
                              }
                              onAdd={() =>
                                handleAddNewTimeRecordForTheSameDay(day)
                              }
                            />
                          ))}
                        </td>
                      )
                    })}
                    <td className="time-entry-hours time-tracking-total-hours">
                      {record.totalHours}h
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ViewLogTimeDrawer
          isOpen={drawerState.isOpen}
          onClose={() => setDrawerState((prev) => ({ ...prev, isOpen: false }))}
          data={drawerState.data}
          onEdit={() => {
            if (drawerState.recordId) {
              setDrawerState((prev) => ({ ...prev, isOpen: false }))
              openLogTimeDrawer(drawerState.recordId)
            }
          }}
          onDelete={() => {
            if (drawerState.recordId) {
              setDrawerState((prev) => ({ ...prev, isOpen: false }))
              handleDeleteTimeRecord(drawerState.recordId)
            }
          }}
        />

        <ConfirmActionModal
          isOpen={pendingDeleteId !== null}
          title={t('timeTracking.deleteEntry.title')}
          message={t('timeTracking.deleteEntry.message')}
          confirmText={t('timeTracking.deleteEntry.confirm')}
          cancelText={t('common.cancel')}
          onConfirm={confirmDelete}
          onClose={() => setPendingDeleteId(null)}
        />
      </div>
    )
  },
)

export const MyTimesTab = (props: MyTimesTabProps) => {
  const [drawerState, setDrawerState] = useState<DrawerState>({
    isOpen: false,
    recordId: null,
    data: null,
  })

  return (
    <MyTimesTabInner
      {...props}
      drawerState={drawerState}
      setDrawerState={setDrawerState}
    />
  )
}
